import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export const exportTokensToExcel = (
  filteredVisits = [],
  filteredTreatmentEntries = [],
  filteredPackagePayments = []
) => {
  if (
    filteredVisits.length === 0 &&
    filteredTreatmentEntries.length === 0 &&
    filteredPackagePayments.length === 0
  ) {
    alert("No data to export");
    return;
  }

  // =========================
  // TOKENS SHEET
  // =========================
  const excelData = [];

  // Visits
  filteredVisits.forEach((v) => {
    excelData.push({
      "S.No": excelData.length + 1,
      "VPJC ID": v.vpjc_id || v.patient_id,
      "Token Number": v.token_number,
      "Patient Name": v.name,
      Mobile: v.mobile,
      "Visiting For": v.visiting_for,
      "Visit Type": v.visit_type,

      "Payment Status": v.consultation_payment_status || "",
      "Payment Mode": v.payment_mode || "",

      Amount:
        v.consultation_payment_status === "TREATMENT"
          ? Number(v.treatment_amount || 0)
          : Number(v.consultation_fee || 0),

      Branch: v.branch_name
        ? v.branch_name.replace(/\s*branch\s*/i, "").trim()
        : "",

      "Visit Date": v.visit_date || "",

      "Created At": v.created_at?.toDate
        ? v.created_at.toDate().toLocaleString("en-IN")
        : "",
    });
  });

  // Treatment Entries
  filteredTreatmentEntries.forEach((t) => {
    if (t.visit_id) return;

    excelData.push({
      "S.No": excelData.length + 1,
      "VPJC ID": t.vpjc_id || t.patient_id,
      "Token Number": "TREATMENT",
      "Patient Name": t.patient_name || "",
      Mobile: t.mobile || "",
      "Visiting For": t.treatment_name || "",
      "Visit Type": "TREATMENT",

      "Payment Status": "PAID",
      "Payment Mode": t.payment_mode || "",

      Amount: Number(t.amount || 0),

      Branch: t.branch_name
        ? t.branch_name.replace(/\s*branch\s*/i, "").trim()
        : "",

      "Visit Date": t.date?.toDate
        ? t.date.toDate().toISOString().split("T")[0]
        : "",

      "Created At": t.created_at?.toDate
        ? t.created_at.toDate().toLocaleString("en-IN")
        : "",
    });
  });

  // Package Payments
  filteredPackagePayments.forEach((p) => {
    excelData.push({
      "S.No": excelData.length + 1,
      "VPJC ID": p.vpjc_id || p.patient_id,
      "Token Number": "PACKAGE",
      "Patient Name": "",
      Mobile: "",
      "Visiting For": "",
      "Visit Type": "PACKAGE PAYMENT",

      "Payment Status": "PAID",
      "Payment Mode": p.payment_mode || "",

      Amount: Number(p.amount || 0),

      Branch: p.branch_name
        ? p.branch_name.replace(/\s*branch\s*/i, "").trim()
        : "",

      "Visit Date": p.created_at?.toDate
        ? p.created_at.toDate().toISOString().split("T")[0]
        : "",

      "Created At": p.created_at?.toDate
        ? p.created_at.toDate().toLocaleString("en-IN")
        : "",
    });
  });

  // =========================
  // BRANCH SUMMARY
  // (copied from TokenSummaryCards)
  // =========================
  const branchSummary = {};

  filteredVisits.forEach((v) => {
    const branch = v.branch_name
      ? v.branch_name.replace(/\s*branch\s*/i, "").trim()
      : "Unknown";

    const amount =
      v.consultation_payment_status === "TREATMENT"
        ? Number(v.treatment_amount || 0)
        : Number(v.consultation_fee || 0);

    if (!branchSummary[branch]) {
      branchSummary[branch] = {
        patients: new Set(),
        cash: 0,
        online: 0,
        total: 0,
      };
    }

    branchSummary[branch].patients.add(
      v.vpjc_id || v.patient_id
    );

    if (v.payment_mode === "CASH") {
      branchSummary[branch].cash += amount;
    } else if (v.payment_mode === "ONLINE") {
      branchSummary[branch].online += amount;
    }

    branchSummary[branch].total += amount;
  });

  filteredTreatmentEntries.forEach((t) => {
    if (t.visit_id) return;

    const branch = t.branch_name
      ? t.branch_name.replace(/\s*branch\s*/i, "").trim()
      : "Unknown";

    const amount = Number(t.amount || 0);

    if (!branchSummary[branch]) {
      branchSummary[branch] = {
        patients: new Set(),
        cash: 0,
        online: 0,
        total: 0,
      };
    }

    branchSummary[branch].patients.add(
      t.vpjc_id || t.patient_id
    );

    if (t.payment_mode === "CASH") {
      branchSummary[branch].cash += amount;
    } else if (t.payment_mode === "ONLINE") {
      branchSummary[branch].online += amount;
    }

    branchSummary[branch].total += amount;
  });

  filteredPackagePayments.forEach((p) => {
    const branch = p.branch_name
      ? p.branch_name.replace(/\s*branch\s*/i, "").trim()
      : "Unknown";

    const amount = Number(p.amount || 0);

    if (!branchSummary[branch]) {
      branchSummary[branch] = {
        patients: new Set(),
        cash: 0,
        online: 0,
        total: 0,
      };
    }

    branchSummary[branch].patients.add(
      p.vpjc_id || p.patient_id
    );

    if (p.payment_mode === "CASH") {
      branchSummary[branch].cash += amount;
    } else if (p.payment_mode === "ONLINE") {
      branchSummary[branch].online += amount;
    }

    branchSummary[branch].total += amount;
  });

  // =========================
  // SUMMARY SHEET
  // =========================
  const summaryData = [];

  const grandPatients = new Set();
  let grandCash = 0;
  let grandOnline = 0;
  let grandTotal = 0;

  Object.entries(branchSummary).forEach(([branch, data]) => {
    summaryData.push({
      Branch: branch,
      "Total Patients": data.patients.size,
      "Total Cash": data.cash,
      "Total Online": data.online,
      "Total Amount": data.total,
    });

    data.patients.forEach((id) => grandPatients.add(id));

    grandCash += data.cash;
    grandOnline += data.online;
    grandTotal += data.total;
  });

  summaryData.push({
    Branch: "TOTAL",
    "Total Patients": grandPatients.size,
    "Total Cash": grandCash,
    "Total Online": grandOnline,
    "Total Amount": grandTotal,
  });

  // =========================
  // EXCEL
  // =========================
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(excelData),
    "Tokens"
  );

  XLSX.utils.book_append_sheet(
    workbook,
    XLSX.utils.json_to_sheet(summaryData),
    "Summary"
  );

  const buffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  saveAs(
    new Blob([buffer], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `VPJC_Tokens_${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};